<template>
    <div class="messaging-analytics">
        <a-spin :spinning="loading">
            <div v-if="!loading && data && data.analytics">
                <div class="toolbar">
                    <div class="buttons">
                        <IconButton
                            :classNames="!tableMode ? 'selected' : ''"
                            :onClick="() => (tableMode = false)"
                            v-tippy="{ content: 'Grafik Görünümü' }"
                        >
                            <md-icon>insights</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="tableMode ? 'selected' : ''"
                            :onClick="() => (tableMode = true)"
                            v-tippy="{ content: 'Tablo Görünümü' }"
                        >
                            <md-icon>table_view</md-icon>
                        </IconButton>
                        <IconButton :onClick="exportToExcel" v-tippy="{ content: 'Excel İndir' }">
                            <md-icon>download</md-icon>
                        </IconButton>
                    </div>
                </div>

                <div class="stats-row">
                    <StatCard title="Toplam Gönderilen" :value="totalSent" unit="mesaj" />
                    <StatCard title="Toplam Teslim Edilen" :value="totalDelivered" unit="mesaj" />
                    <StatCard title="Teslim Oranı" :value="deliveryRate" unit="%" />
                </div>

                <div class="chart-container" v-if="!tableMode">
                    <StackedAreaChart
                        title="Mesaj İstatistikleri"
                        :x-axis-data="getXAxisData()"
                        :legends="['Gönderilen', 'Teslim Edilen']"
                        :colors="['#37A2FF', '#80FFA5']"
                        :series="getSeries()"
                    />
                </div>

                <div class="table-container" v-else>
                    <a-table :columns="columns" :data-source="tableData" :pagination="{ pageSize: 10 }" size="middle" />
                </div>

                <div
                    class="breakdown-section"
                    v-if="data.analytics.country_codes && data.analytics.country_codes.length > 0"
                >
                    <h3>Ülke Bazlı Dağılım</h3>
                    <div class="country-list">
                        <div v-for="country in data.analytics.country_codes" :key="country" class="country-item">
                            <span class="country-code">{{ country }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="!loading" class="empty-state">
                <a-empty description="Veri bulunamadı. Lütfen tarih aralığını kontrol edin." />
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import { expertToExcel } from "../../../../../utils/utils";
import StackedAreaChart from "../../../Chatbot/pages/reports/charts/StackedAreaChart.vue";
import PieChartV2 from "../../../Chatbot/pages/reports/charts/PieChartV2.vue";
import StatCard from "../../../Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../../Settings/components/IconButton.vue";

export default Vue.extend({
    name: "MessagingAnalytics",
    components: {
        StackedAreaChart,
        StatCard,
        IconButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        filters: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            tableMode: false,
            columns: [
                {
                    title: "Tarih",
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: "Gönderilen",
                    dataIndex: "sent",
                    key: "sent",
                    sorter: (a, b) => a.sent - b.sent,
                },
                {
                    title: "Teslim Edilen",
                    dataIndex: "delivered",
                    key: "delivered",
                    sorter: (a, b) => a.delivered - b.delivered,
                },
                {
                    title: "Teslim Oranı",
                    dataIndex: "rate",
                    key: "rate",
                },
            ],
        };
    },
    computed: {
        dataPoints() {
            return _.get(this.data, "analytics.data_points", []) || [];
        },
        totalSent() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.sent || 0), 0);
        },
        totalDelivered() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.delivered || 0), 0);
        },
        deliveryRate() {
            if (this.totalSent === 0) return 0;
            return ((this.totalDelivered / this.totalSent) * 100).toFixed(2);
        },
        tableData() {
            return this.dataPoints.map((dp, index) => ({
                key: index,
                date: moment.unix(dp.start).format("DD/MM/YYYY HH:mm"),
                sent: dp.sent || 0,
                delivered: dp.delivered || 0,
                rate: dp.sent > 0 ? ((dp.delivered / dp.sent) * 100).toFixed(2) + "%" : "0%",
            }));
        },
    },
    methods: {
        getXAxisData() {
            const granularity = this.filters.granularity;
            return this.dataPoints.map((dp) => {
                if (granularity === "HALF_HOUR") {
                    return moment.unix(dp.start).format("HH:mm");
                } else if (granularity === "MONTH") {
                    return moment.unix(dp.start).format("MMM YYYY");
                }
                return moment.unix(dp.start).format("DD/MM");
            });
        },
        getSeries() {
            return [
                {
                    name: "Gönderilen",
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: { width: 0 },
                    showSymbol: false,
                    areaStyle: { opacity: 0.8 },
                    emphasis: { focus: "series" },
                    data: this.dataPoints.map((dp) => dp.sent || 0),
                },
                {
                    name: "Teslim Edilen",
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: { width: 0 },
                    showSymbol: false,
                    areaStyle: { opacity: 0.8 },
                    emphasis: { focus: "series" },
                    data: this.dataPoints.map((dp) => dp.delivered || 0),
                },
            ];
        },
        exportToExcel() {
            const exportData = this.tableData.map((row) => ({
                Tarih: row.date,
                Gönderilen: row.sent,
                "Teslim Edilen": row.delivered,
                "Teslim Oranı": row.rate,
            }));
            expertToExcel(exportData, "messaging_analytics.xlsx", "Messaging Analytics");
        },
    },
});
</script>

<style scoped lang="scss">
.messaging-analytics {
    padding: 20px;

    .toolbar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 20px;

        .buttons {
            display: flex;
            border: 1px solid var(--color-border-default);
            border-radius: 4px;

            .icon-button {
                padding: 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                border-right: 1px solid var(--color-border-default);
                border-radius: unset !important;

                &.selected {
                    background-color: var(--color-bg-info-low-hover);
                }

                &:last-child {
                    border-right: none;
                }

                &:hover {
                    background-color: var(--color-bg-neutral-pressed);
                }
            }
        }
    }

    .stats-row {
        display: flex;
        gap: 20px;
        margin-bottom: 30px;
        flex-wrap: wrap;

        .stat-card {
            flex: 1;
            min-width: 200px;
        }
    }

    .chart-container {
        min-height: 400px;
        margin-bottom: 30px;
    }

    .table-container {
        margin-bottom: 30px;
    }

    .breakdown-section {
        margin-top: 30px;
        padding-top: 20px;
        border-top: 1px solid var(--color-border-default);

        h3 {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 15px;
        }

        .country-list {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;

            .country-item {
                padding: 8px 16px;
                background: var(--color-bg-subtle);
                border-radius: 4px;

                .country-code {
                    font-weight: 600;
                }
            }
        }
    }

    .empty-state {
        padding: 60px 20px;
        text-align: center;
    }
}
</style>
